(() => {
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const buildUrl = (baseUrl, idx, pad, ext) => {
    const n = String(idx).padStart(pad, "0");
    return baseUrl + n + ext;
  };

  function collectSteps(root, lastFrame) {
    const nodes = Array.from(root.querySelectorAll("[data-step]"));

    // First collect start/end (end may be missing/0)
    const steps = nodes
      .map((el) => {
        const card = el.querySelector(".evr-scrollseq__card");
        const start = parseInt(el.getAttribute("data-start-frame") || "0", 10);
        const end = parseInt(el.getAttribute("data-end-frame") || "0", 10); // optional
        return {
          el,
          card,
          startFrame: Number.isFinite(start) ? start : 0,
          endFrame: Number.isFinite(end) ? end : 0,
        };
      })
      .sort((a, b) => a.startFrame - b.startFrame);

    // Auto-derive missing/invalid endFrame from next startFrame
    for (let i = 0; i < steps.length; i++) {
      const s = steps[i];

      // Clamp start to bounds
      s.startFrame = clamp(s.startFrame, 0, lastFrame);

      const next = steps[i + 1];
      const impliedEnd = next ? clamp(next.startFrame - 1, 0, lastFrame) : lastFrame;

      // If endFrame not provided or invalid, use impliedEnd
      if (!(s.endFrame > s.startFrame)) {
        s.endFrame = impliedEnd;
      } else {
        // Clamp provided end
        s.endFrame = clamp(s.endFrame, 0, lastFrame);
        if (s.endFrame < s.startFrame) s.endFrame = impliedEnd;
      }
    }

    // Filter out truly empty steps after normalization (rare)
    return steps.filter((s) => s.endFrame >= s.startFrame);
  }

  function stepVisibilityByFrame(virtualFrame, step, fadeFrames) {
    const s = step.startFrame,
      e = step.endFrame;
    const fade = Math.max(0, fadeFrames | 0);

    if (fade === 0) return virtualFrame >= s && virtualFrame <= e ? 1 : 0;

    if (virtualFrame < s - fade || virtualFrame > e + fade) return 0;
    if (virtualFrame >= s && virtualFrame <= e) return 1;

    if (virtualFrame < s) {
      return clamp((virtualFrame - (s - fade)) / fade, 0, 1);
    }
    return clamp(1 - (virtualFrame - e) / fade, 0, 1);
  }

  function initSection(root) {
    if (root.__evrInited) return;
    root.__evrInited = true;

    const baseUrlDesktop = root.getAttribute("data-base-url") || "";
    const baseUrlMobile  = root.getAttribute("data-base-url-mobile") || ""; // optional
    const ext = root.getAttribute("data-ext") || ".webp";
    const pad = parseInt(root.getAttribute("data-pad") || "4", 10);
    const startIndex = parseInt(root.getAttribute("data-start-index") || "1", 10);
    const frameCount = parseInt(root.getAttribute("data-frame-count") || "1", 10);
    const maxDpr = parseFloat(root.getAttribute("data-max-dpr") || "2");
    const preloadWindow = parseInt(root.getAttribute("data-preload-window") || "10", 10);
    const fadeFrames = parseInt(root.getAttribute("data-fade-frames") || "8", 10);

    const canvas = root.querySelector(".evr-scrollseq__canvas");
    const fallbackImg = root.querySelector(".evr-scrollseq__fallback");
    const ctx = canvas && canvas.getContext && canvas.getContext("2d");

    const mediaScale = parseFloat(canvas?.getAttribute("data-media-scale") || "1") || 1;

    const lastFrame = Math.max(0, frameCount - 1);
    const steps = collectSteps(root, lastFrame);

    const mmMobile = window.matchMedia("(max-width: 950px)");

    let activeBaseUrl = "";
    const selectBaseUrl = () => {
      // IMPORTANT: if mobile base url is missing, fall back to desktop instead of returning ""
      if (mmMobile.matches && baseUrlMobile) return baseUrlMobile;
      return baseUrlDesktop || baseUrlMobile || "";
    };


    const cache = new Map();
    const inflight = new Map();
    let currentRenderedFrame = -1;
    let rafPending = false;

    const ensureActiveSource = () => {
      const next = selectBaseUrl();

      // If still empty, do nothing (but now it should almost never be empty)
      if (!next) return;

      // Initialize immediately if not set yet
      if (!activeBaseUrl) {
        activeBaseUrl = next;
        return;
      }

      // Switch (desktop<->mobile) => reset caches
      if (next !== activeBaseUrl) {
        activeBaseUrl = next;
        cache.clear();
        inflight.clear();
        currentRenderedFrame = -1;
        root.__evrAspectSet = false;
      }
    };

    if (!ctx) {
      ensureActiveSource();

      if (fallbackImg) {
        fallbackImg.style.display = "block";
        fallbackImg.src = buildUrl(activeBaseUrl, startIndex, pad, ext);
      }
      return;
    }

  const loadImage = (frame) => {
    frame = clamp(frame, 0, lastFrame);

    // ensure correct source for current breakpoint
    ensureActiveSource();

    if (cache.has(frame)) return Promise.resolve(cache.get(frame));
    if (inflight.has(frame)) return inflight.get(frame);

    const url = buildUrl(activeBaseUrl, startIndex + frame, pad, ext);

    const p = new Promise((resolve, reject) => {
      const img = new Image();
      img.decoding = "async";
      img.loading = "eager";
      img.onload = () => {
        cache.set(frame, img);
        inflight.delete(frame);
        resolve(img);
      };
      img.onerror = (e) => {
        inflight.delete(frame);
        reject(e);
      };
      img.src = url;
    });

    inflight.set(frame, p);
    return p;
  };

  const setCanvasSizeAndDraw = (img) => {
    const dpr = clamp(window.devicePixelRatio || 1, 1, maxDpr);
    const cssW = canvas.clientWidth || 1;
    const cssH = canvas.clientHeight || 1;

    const w = Math.round(cssW * dpr);
    const h = Math.round(cssH * dpr);

    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }

    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;

    if (!root.__evrAspectSet && iw > 0 && ih > 0) {
      root.style.setProperty("--evr-media-aspect", `${iw} / ${ih}`);
      root.__evrAspectSet = true;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const baseScale = Math.min(canvas.width / iw, canvas.height / ih);
    const scale = baseScale * mediaScale;

    const dw = Math.round(iw * scale);
    const dh = Math.round(ih * scale);

    const dx = Math.round((canvas.width - dw) / 2);
    const dy = Math.round((canvas.height - dh) / 2);

    ctx.drawImage(img, dx, dy, dw, dh);
  };

    const drawFrame = (frame) => {
        frame = clamp(frame, 0, lastFrame);
        if (frame === currentRenderedFrame) return;

        loadImage(frame)
            .then((img) => {
            currentRenderedFrame = frame;

            // if fallback was ever shown, hide it again
            if (fallbackImg) fallbackImg.style.display = "none";

            setCanvasSizeAndDraw(img);
            })
            .catch(() => {
            // Only show fallback if we never rendered anything at all
            if (currentRenderedFrame < 0 && fallbackImg) {
                ensureActiveSource();
                fallbackImg.src = buildUrl(activeBaseUrl, startIndex, pad, ext);
                fallbackImg.style.display = "block";
            }
            // Otherwise: ignore the failed frame and keep last good canvas render
        });
    };

    const preloadAround = (frame) => {
      const from = clamp(frame - preloadWindow, 0, lastFrame);
      const to = clamp(frame + preloadWindow, 0, lastFrame);

      for (let f = from; f <= to; f++) loadImage(f).catch(() => {});

      const keepFrom = clamp(frame - preloadWindow * 2, 0, lastFrame);
      const keepTo = clamp(frame + preloadWindow * 2, 0, lastFrame);
      for (const key of cache.keys()) {
        if (key < keepFrom || key > keepTo) cache.delete(key);
      }
    };

    const getProgress = () => {
      const spacer = root.querySelector(".evr-scrollseq__spacer");
      const rect = spacer.getBoundingClientRect();
      const viewportH = window.innerHeight || 1;
      const total = rect.height - viewportH || 1;
      const scrolled = -rect.top;
      return clamp(scrolled / total, 0, 1);
    };

  const updateSteps = (virtualFrame) => {
    // Reuse your existing fadeFrames as the ENTER travel window (in frames)
    const enterFrames = Math.max(1, fadeFrames | 0);

    // Exit should be quicker than enter; derive a small window from fadeFrames
    const exitFrames = Math.max(3, Math.round(enterFrames / 3)); // e.g. 8 -> 3, 15 -> 5

    // Off-screen travel distance (px). Using viewport height makes it truly outside screen.
    const enterFromPx = Math.round((window.innerHeight || 800) * 1.05);

    // Slight up when exiting
    const exitUpPx = 14;

    steps.forEach((s) => {
      const start = s.startFrame;
      const end = s.endFrame;

      const visibleFrom = start - enterFrames;
      const visibleTo = end + exitFrames;

      // Fully outside active + animation bands => hide hard
      if (virtualFrame < visibleFrom || virtualFrame > visibleTo) {
        s.el.classList.remove("is-active");
        s.el.style.visibility = "hidden";
        if (s.card) {
          s.card.style.opacity = "";
          s.card.style.transform = "";
        }
        return;
      }

      // Visible
      s.el.classList.add("is-active");
      s.el.style.visibility = "visible";

      if (!s.card) return;

      // ENTER: [start-enterFrames .. start] — move up frame-by-frame, NO fade
      if (virtualFrame < start) {
        const t = clamp((virtualFrame - (start - enterFrames)) / enterFrames, 0, 1); // 0->1
        const y = enterFromPx * (1 - t); // enterFromPx -> 0
        s.card.style.opacity = "1";
        s.card.style.transform = `translateY(${y.toFixed(2)}px)`;
        return;
      }

      // HOLD: [start .. end]
      if (virtualFrame <= end) {
        s.card.style.opacity = "1";
        s.card.style.transform = "translateY(0px)";
        return;
      }

      // EXIT: [end .. end+exitFrames] — quick fade + slight up
      const t = clamp((virtualFrame - end) / exitFrames, 0, 1); // 0->1
      const y = -exitUpPx * t; // 0 -> -exitUpPx
      const o = 1 - t;         // 1 -> 0 quickly
      s.card.style.opacity = String(o);
      s.card.style.transform = `translateY(${y.toFixed(2)}px)`;
    });
  };

    const tick = () => {
      rafPending = false;

      ensureActiveSource();

      const progress = getProgress();
      const virtualFrame = clamp(Math.round(progress * lastFrame), 0, lastFrame);

      drawFrame(virtualFrame);
      preloadAround(virtualFrame);
      updateSteps(virtualFrame);
    };

    const requestTick = () => {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(tick);
    };

    // --- Mobile/desktop source switch on breakpoint change ---
    const onMqChange = () => requestTick();
    if (mmMobile.addEventListener) mmMobile.addEventListener("change", onMqChange);
    else mmMobile.addListener(onMqChange); // Safari fallback

    // Ensure we pick the correct base url (desktop vs mobile) before setting fallback src
    ensureActiveSource();

    if (fallbackImg) {
      fallbackImg.src = buildUrl(activeBaseUrl, startIndex, pad, ext);
      fallbackImg.style.display = "none";
    }

    preloadAround(0);

    // Force a redraw on the next frame after layout settles (mobile)
    if (mmMobile.matches) root.__evrForceRedraw = true;
    requestTick();

    window.addEventListener("scroll", requestTick, { passive: true });
    window.addEventListener("resize", requestTick);

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) requestTick();
        }
      },
      { threshold: 0.01 }
    );

    io.observe(root);
  }

  function initAll() {
    document.querySelectorAll("[data-evr-scrollseq]").forEach(initSection);
  }

  document.addEventListener("DOMContentLoaded", initAll);
  document.addEventListener("shopify:section:load", initAll);
})();